import { isDischargeActionField } from '@/utils/noteFieldActions';
// apps/provider/src/utils/formPopulationMapper.js
//
// Applies form population mappings to template sectionsData.
// Runs AFTER carryforward — only fills fields that are still empty.
// Modeled after planOfCareMapper.js.

/**
 * Normalize formIds from a FormPopulationMapping config (backwards compat with legacy formId).
 */
function getFormIds(config) {
  if (config.formIds?.length) return config.formIds;
  if (config.formId) return [config.formId];
  return [];
}

/**
 * Normalize questionIds from a FormPopulationMapping config (backwards compat with legacy questionId).
 */
function getQuestionIds(config) {
  if (config.questionIds?.length) return config.questionIds;
  if (config.questionId) return [config.questionId];
  return [];
}

/**
 * Apply form population mappings to sectionsData.
 * Walks template sections → fields with formPopulation config, resolves the
 * matching form response (or report), extracts the answer, converts it to a
 * field-compatible value, and sets it on the field entry.
 *
 * @param {Object} template - The NoteTemplate with sections[].fields[]
 * @param {Object} sectionsData - The note's sections_data (NoteSectionData shape)
 * @param {Array} formResponses - All of the patient's form responses
 * @param {Array} [formReports] - Generated form reports (for source='report' fields)
 * @param {number|null} [caseId] - The note's case. Responses stamped with a different
 *   case are ignored; a case-less note only sees unscoped responses. Reports are not
 *   case-scoped (form_generated_reports has no case_id).
 * @returns {Object} Updated sectionsData (new object, immutable)
 */
export function applyFormPopulation(template, sectionsData, formResponses, formReports, caseId = null) {
  if (!template?.sections || !sectionsData?.sections) {
    return sectionsData;
  }

  // Deep clone to avoid mutation
  const updated = JSON.parse(JSON.stringify(sectionsData));

  // Cache resolved responses by formId to avoid redundant lookups
  const responseCache = {};

  for (const section of template.sections) {
    if (!section.fields) continue;

    const sectionData = updated.sections.find(s => s.sectionId === section.id);
    if (!sectionData) continue;

    for (const field of section.fields) {
      if (isDischargeActionField(field) || !field.formPopulation) continue;

      const config = field.formPopulation;

      let fieldEntry = sectionData.fields.find(f => f.fieldId === field.id);

      // Flow sheets populate per ITEM slot (emptiness is per slot, not per
      // field, so a carried-forward sheet can still receive an outcome score)
      if (field.type === 'flow_sheet') {
        applyFlowSheetItemMappings(field, config, sectionData, fieldEntry, formResponses, responseCache, caseId);
        continue;
      }

      // Find the field entry — skip if already has a value (carryforward wins)
      if (fieldEntry && hasValue(fieldEntry.value)) continue;

      // Dispatch by source type
      if (config.source === 'report') {
        // Report-based population
        if (!formReports?.length) continue;

        const reportValue = findReportValue(config, formReports, responseCache);
        if (reportValue == null) continue;

        const convertedValue = convertReportValue(field, reportValue);
        if (convertedValue == null) continue;

        if (fieldEntry) {
          fieldEntry.value = convertedValue;
          fieldEntry.populatedFromReport = true;
          fieldEntry.sourceReportId = reportValue._reportId;
          fieldEntry.populatedFromForm = true;
          if (config.mode === 'auto_review') {
            fieldEntry.confirmedByProvider = false;
          }
        } else {
          sectionData.fields.push({
            fieldId: field.id,
            value: convertedValue,
            populatedFromReport: true,
            sourceReportId: reportValue._reportId,
            populatedFromForm: true,
            confirmedByProvider: config.mode === 'auto_review' ? false : undefined,
          });
        }
      } else if (config.source === 'variable') {
        // Variable-based population: pull calculated variable from form response
        if (!formResponses?.length) continue;

        const response = resolveFormResponse(
          config, formResponses, responseCache, caseId
        );
        if (!response) continue;

        const calcVar = findVariableValue(response, config.variableId);
        if (!calcVar) continue;

        const convertedValue = convertVariableValue(field, calcVar);
        if (convertedValue == null) continue;

        if (fieldEntry) {
          fieldEntry.value = convertedValue;
          fieldEntry.populatedFromForm = true;
          fieldEntry.sourceFormResponseId = response.id;
          if (config.mode === 'auto_review') {
            fieldEntry.confirmedByProvider = false;
          }
        } else {
          sectionData.fields.push({
            fieldId: field.id,
            value: convertedValue,
            populatedFromForm: true,
            sourceFormResponseId: response.id,
            confirmedByProvider: config.mode === 'auto_review' ? false : undefined,
          });
        }
      } else {
        // Default: response-based population
        if (!formResponses?.length) continue;

        const response = resolveFormResponse(
          config, formResponses, responseCache, caseId
        );
        if (!response) continue;

        // Try each configured question in order — first non-empty answer wins
        let convertedValue = null;
        for (const qId of getQuestionIds(config)) {
          const answer = findQuestionAnswer(response, qId);
          if (answer == null) continue;
          const candidate = convertFormValue(field, config, answer);
          if (candidate != null && hasValue(candidate)) {
            convertedValue = candidate;
            break;
          }
        }
        if (convertedValue == null) continue;

        if (fieldEntry) {
          fieldEntry.value = convertedValue;
          fieldEntry.populatedFromForm = true;
          fieldEntry.sourceFormResponseId = response.id;
          if (config.mode === 'auto_review') {
            fieldEntry.confirmedByProvider = false;
          }
        } else {
          sectionData.fields.push({
            fieldId: field.id,
            value: convertedValue,
            populatedFromForm: true,
            sourceFormResponseId: response.id,
            confirmedByProvider: config.mode === 'auto_review' ? false : undefined
          });
        }
      }
    }
  }

  return updated;
}

/**
 * Flow sheet item population. Each mapping writes one slot
 * (staticValues[itemId][side]) from the resolved response: a question answer
 * (source 'response', questionIds tried in order) or a calculated variable
 * (source 'variable'). Only empty slots are written; two mappings for the same
 * slot act as fallbacks. Values are stored as the string the editor would
 * hold (select-type items keep the answer text as-is).
 */
function applyFlowSheetItemMappings(field, config, sectionData, fieldEntry, formResponses, cache, caseId) {
  const mappings = Array.isArray(config?.itemMappings) ? config.itemMappings : [];
  if (mappings.length === 0 || !formResponses?.length) return;
  if (config.source === 'report') return; // reports are not supported for item slots

  const response = resolveFormResponse(config, formResponses, cache, caseId);
  if (!response) return;

  const existing = fieldEntry?.value && typeof fieldEntry.value === 'object' && !Array.isArray(fieldEntry.value)
    ? fieldEntry.value
    : { staticValues: {}, exerciseRows: [], categoryComments: {}, itemGoals: {} };
  const staticValues = { ...(existing.staticValues || {}) };
  let wrote = false;

  for (const m of mappings) {
    if (!m?.itemId) continue;
    const slot = m.side === 'left' || m.side === 'right' ? m.side : 'value';
    const cur = staticValues[m.itemId] || {};
    if (hasValue(cur[slot])) continue;

    let raw = null;
    if (config.source === 'variable') {
      const calcVar = findVariableValue(response, m.variableId);
      raw = calcVar?.value;
    } else {
      for (const qId of (m.questionIds || [])) {
        const answer = findQuestionAnswer(response, qId);
        if (answer == null) continue;
        const candidate = extractAnswerRaw(config, answer);
        if (candidate != null && hasValue(candidate)) {
          raw = candidate;
          break;
        }
      }
    }
    if (raw == null || raw === '') continue;

    const value = Array.isArray(raw) ? raw.map(String).join(', ') : String(raw);
    staticValues[m.itemId] = { ...cur, [slot]: value };
    wrote = true;
  }

  if (!wrote) return;
  const nextValue = { ...existing, staticValues };
  if (fieldEntry) {
    fieldEntry.value = nextValue;
    fieldEntry.populatedFromForm = true;
    fieldEntry.sourceFormResponseId = response.id;
    if (config.mode === 'auto_review') fieldEntry.confirmedByProvider = false;
  } else {
    sectionData.fields.push({
      fieldId: field.id,
      value: nextValue,
      populatedFromForm: true,
      sourceFormResponseId: response.id,
      confirmedByProvider: config.mode === 'auto_review' ? false : undefined,
    });
  }
}

/**
 * Check if a field value is non-empty.
 */
function hasValue(value) {
  if (value === undefined || value === null || value === '') return false;
  if (typeof value === 'string' && !value.trim()) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (value === false) return false;
  return true;
}

/**
 * Resolve the most recently completed form response matching any of the config's form IDs.
 *
 * @param {Object} config - FormPopulationMapping
 * @param {Array} formResponses - All patient form responses
 * @param {Object} cache - Mutable cache object
 * @param {number|null} [caseId] - The note's case (see applyFormPopulation)
 * @returns {Object|null} The resolved form response, or null
 */
function resolveFormResponse(config, formResponses, cache, caseId = null) {
  const ids = getFormIds(config);
  const cacheKey = `forms_${ids.join(',')}_${caseId || 'none'}`;
  if (cache[cacheKey] !== undefined) return cache[cacheKey];

  const sameCase = (r) => r.case_id != null && caseId && String(r.case_id) === String(caseId);

  const candidates = formResponses
    .filter(r => ids.includes(r.form_id) && r.status === 'complete')
    // With a case: drop responses stamped with a different case (unscoped still allowed).
    // Without a case: drop every case-stamped response.
    .filter(r => {
      if (r.case_id == null) return true;
      if (!caseId) return false;
      return String(r.case_id) === String(caseId);
    })
    .sort((a, b) => {
      const caseRank = (sameCase(a) ? 0 : 1) - (sameCase(b) ? 0 : 1);
      if (caseRank !== 0) return caseRank;
      const aTime = typeof a.created_at === 'number' ? a.created_at : new Date(a.created_at).getTime();
      const bTime = typeof b.created_at === 'number' ? b.created_at : new Date(b.created_at).getTime();
      return bTime - aTime;
    });

  const result = candidates[0] || null;
  cache[cacheKey] = result;
  return result;
}

/**
 * Find a question's answer object within a form response.
 * Form responses store answers as an array of { question_id, answer, answer_text, numerical_value }.
 *
 * @param {Object} response - The form response
 * @param {string} questionId - The question_id to look for
 * @returns {Object|null} The answer object, or null
 */
function findQuestionAnswer(response, questionId) {
  // Check question_answers array (standard form responses)
  if (Array.isArray(response?.question_answers)) {
    const match = response.question_answers.find(a => a.question_id === questionId);
    if (match) return match;
  }

  // Check raw_answers keyed object. Entries here are the bare answer value
  // (e.g. an array of body-diagram selections), NOT an { answer, answer_text }
  // object — wrap them so convertFormValue can read .answer/.answer_text.
  if (response?.raw_answers && typeof response.raw_answers === 'object') {
    const entry = response.raw_answers[questionId];
    if (entry != null) {
      if (typeof entry === 'object' && !Array.isArray(entry) && 'answer' in entry) {
        return entry;
      }
      return { answer: entry, answer_text: entry };
    }
  }

  return null;
}

/**
 * Coerce a raw value into a body-diagram selections array.
 * Accepts a native array or a JSON-stringified array; anything else → null.
 */
function toDiagramSelections(rawValue) {
  if (Array.isArray(rawValue)) return rawValue;
  if (typeof rawValue === 'string') {
    try {
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

// Auto-generated option values follow this reserved format; anything else on a
// choice answer's raw_value is an authored provider value.
const AUTO_OPTION_VALUE = /^option_\d+$/;

/**
 * Resolve a choice answer's value, preferring authored provider values.
 * raw_value and answer are parallel-ordered (both built from the selection
 * array at submission), so each auto-generated `option_N` element falls back
 * to its display text counterpart. Scalar in → scalar out, array in → array out.
 */
function resolveChoiceValue(answerObj) {
  const rawVals = Array.isArray(answerObj.raw_value) ? answerObj.raw_value : [answerObj.raw_value];
  const displays = Array.isArray(answerObj.answer) ? answerObj.answer : [answerObj.answer];

  const resolved = rawVals.map((rawVal, i) => {
    if (typeof rawVal === 'string' && AUTO_OPTION_VALUE.test(rawVal)) {
      return displays[i] ?? rawVal;
    }
    return rawVal;
  });

  return Array.isArray(answerObj.raw_value) ? resolved : resolved[0];
}

/**
 * Convert a form answer to a field-compatible value based on field type and valueSource.
 *
 * @param {Object} field - The NoteTemplateField
 * @param {Object} config - FormPopulationMapping
 * @param {Object} answerObj - The answer object { answer, answer_text, numerical_value, raw_value }
 * @returns {*} The converted value, or null if not convertible
 */
/**
 * Pull the raw answer out of an answer object per the config's valueSource.
 * Shared by field-level conversion and flow sheet item mappings.
 */
function extractAnswerRaw(config, answerObj) {
  // 'option_value' is deprecated; provider values now apply automatically under 'raw'
  const configured = config.valueSource === 'option_value' ? 'raw' : config.valueSource;
  const valueSource = configured || 'raw';

  if (valueSource === 'numerical_value') {
    return answerObj.numerical_value ?? answerObj.answer;
  }
  if (answerObj.raw_value != null) {
    // Choice question (raw_value is only set for multipleChoice/checkbox/select):
    // authored provider values take precedence over display text
    return resolveChoiceValue(answerObj);
  }
  if (valueSource === 'answer_text') {
    return answerObj.answer_text ?? answerObj.answer;
  }
  return answerObj.answer;
}

function convertFormValue(field, config, answerObj) {
  const rawValue = extractAnswerRaw(config, answerObj);

  if (rawValue == null) return null;

  switch (field.type) {
    case 'body_diagram':
      return toDiagramSelections(rawValue);

    case 'text':
    case 'textarea': {
      if (Array.isArray(rawValue)) return rawValue.join(', ');
      return String(rawValue);
    }

    case 'select':
    case 'radio_group': {
      // Try to match raw value to field options
      const matchByValue = field.options?.find(opt => opt.value === String(rawValue));
      if (matchByValue) return matchByValue.value;

      // Try matching by display text (using answer_text)
      const displayText = answerObj.answer_text || String(rawValue);
      const matchByDisplay = field.options?.find(
        opt => opt.display.toLowerCase() === displayText.toLowerCase()
      );
      if (matchByDisplay) return matchByDisplay.value;

      // No match — return null to skip population
      return null;
    }

    case 'checkbox_group': {
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      // Filter to only include values that exist in the field's options
      const validValues = values
        .map(v => String(v))
        .filter(v => field.options?.some(opt => opt.value === v));
      return validValues.length > 0 ? validValues : null;
    }

    case 'checkbox':
      return Boolean(rawValue);

    case 'date_composite':
      return String(rawValue);

    case 'data_grid': {
      if (Array.isArray(rawValue)) return rawValue.length > 0 ? rawValue : null;
      if (typeof rawValue === 'string') {
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch { /* not JSON */ }
      }
      return null;
    }

    default:
      return String(rawValue);
  }
}

/**
 * Normalize reportItemMappings from a FormPopulationMapping config (backwards compat with legacy reportItemMapping).
 */
function getReportItemMappings(config) {
  if (config.reportItemMappings?.length) return config.reportItemMappings;
  if (config.reportItemMapping) return [config.reportItemMapping];
  return [];
}

/**
 * Resolve a single mapping against a single report.
 *
 * @param {Object} report - A generated form report
 * @param {Object} mapping - ReportItemMapping
 * @returns {Object|null} The report item, or null
 */
function resolveReportItem(report, mapping) {
  let item = null;

  // Try to find by reportSectionId + reportItemId
  if (mapping.reportSectionId && mapping.reportItemId && report.sections) {
    const section = report.sections.find(s => s.id === mapping.reportSectionId);
    const sectionItems = section?.content?.items || section?.content?.elements || section?.items;
    if (sectionItems) {
      item = sectionItems.find(i => i.id === mapping.reportItemId);
    }
  }

  // Fallback: search all items by sourceQuestionId
  if (!item && mapping.sourceQuestionId && report.sections) {
    for (const section of report.sections) {
      const sectionItems = section.content?.items || section.content?.elements || section.items;
      if (!sectionItems) continue;
      item = sectionItems.find(
        i => i.question_id === mapping.sourceQuestionId || i.source_question_id === mapping.sourceQuestionId
      );
      if (item) break;
    }
  }

  // Fallback: search by sourceVariableId
  if (!item && mapping.sourceVariableId && report.sections) {
    for (const section of report.sections) {
      const sectionItems = section.content?.items || section.content?.elements || section.items;
      if (!sectionItems) continue;
      item = sectionItems.find(
        i => i.variable_id === mapping.sourceVariableId || i.variable_name === mapping.sourceVariableId
      );
      if (item) break;
    }
  }

  // Also check top-level calculated_variables on report
  if (!item && mapping.sourceVariableId && report.calculated_variables) {
    const calcVar = report.calculated_variables.find(
      v => v.variable_id === mapping.sourceVariableId || v.variable_name === mapping.sourceVariableId
    );
    if (calcVar) {
      item = { value: calcVar.value, display_value: String(calcVar.value) };
    }
  }

  return item || null;
}

/**
 * Find a value in generated form reports based on the config's report item mappings.
 * Reports are tried newest-first; within each report, each mapping is tried in
 * config order. The first resolved item wins — so forms with duplicate report
 * sections (different IDs per form) resolve against whichever report the
 * patient actually has.
 *
 * @param {Object} config - FormPopulationMapping with source='report'
 * @param {Array} formReports - Generated form reports
 * @param {Object} cache - Mutable cache
 * @returns {Object|null} Report item with _reportId added, or null
 */
function findReportValue(config, formReports, cache) {
  const ids = getFormIds(config);
  const mappings = getReportItemMappings(config);
  const cacheKey = `report_${ids.join(',')}_${JSON.stringify(mappings)}`;
  if (cache[cacheKey] !== undefined) return cache[cacheKey];

  const candidateReports = formReports
    .filter(r => ids.includes(r.form_id) || ids.includes(r.form_definition_id))
    .sort((a, b) => {
      const aTime = typeof a.created_at === 'number' ? a.created_at : new Date(a.created_at).getTime();
      const bTime = typeof b.created_at === 'number' ? b.created_at : new Date(b.created_at).getTime();
      return bTime - aTime;
    });

  let result = null;
  for (const report of candidateReports) {
    for (const mapping of mappings) {
      const item = resolveReportItem(report, mapping);
      if (item) {
        result = { ...item, _reportId: report.id };
        break;
      }
    }
    if (result) break;
  }

  cache[cacheKey] = result;
  return result;
}

/**
 * Convert a report item to a field-compatible value.
 *
 * @param {Object} field - The NoteTemplateField
 * @param {Object} reportItem - The report item (with value, display_value, etc.)
 * @returns {*} The converted value, or null
 */
function convertReportValue(field, reportItem) {
  if (!reportItem) return null;

  // Body diagram: the selections array lives on .value — display_value/content
  // hold a string summary that would shadow it in the generic pick below.
  if (field.type === 'body_diagram') {
    return toDiagramSelections(reportItem.value)
      ?? toDiagramSelections(reportItem.display_value)
      ?? toDiagramSelections(reportItem.content);
  }

  // Use display_value for text fields, raw value for others; also check .content for AI-generated elements
  const rawValue = reportItem.display_value || reportItem.value || reportItem.content;
  if (rawValue == null) return null;

  // AI-generated content may be a string array (list format) — join into text
  if (Array.isArray(rawValue)) {
    return rawValue.join('\n');
  }

  switch (field.type) {

    case 'text':
    case 'textarea':
      return String(rawValue);

    case 'select':
    case 'radio_group': {
      const strValue = String(rawValue);
      const matchByValue = field.options?.find(opt => opt.value === strValue);
      if (matchByValue) return matchByValue.value;
      const matchByDisplay = field.options?.find(
        opt => opt.display.toLowerCase() === strValue.toLowerCase()
      );
      if (matchByDisplay) return matchByDisplay.value;
      return null;
    }

    case 'checkbox_group': {
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      const validValues = values
        .map(v => String(v))
        .filter(v => field.options?.some(opt => opt.value === v));
      return validValues.length > 0 ? validValues : null;
    }

    case 'checkbox':
      return Boolean(rawValue);

    case 'data_grid': {
      if (Array.isArray(rawValue)) return rawValue.length > 0 ? rawValue : null;
      return null;
    }

    default:
      return String(rawValue);
  }
}

/**
 * Find a calculated variable value within a form response.
 *
 * @param {Object} response - The form response
 * @param {string} variableId - The variable_id or variable_name to look for
 * @returns {Object|null} The CalculatedVariable object, or null
 */
function findVariableValue(response, variableId) {
  if (!response?.calculated_variables || !variableId) return null;
  return response.calculated_variables.find(
    v => v.variable_id === variableId || v.variable_name === variableId
  ) || null;
}

/**
 * Convert a calculated variable to a field-compatible value.
 *
 * @param {Object} field - The NoteTemplateField
 * @param {Object} calcVar - The CalculatedVariable { value, variable_id, variable_name, variable_type }
 * @returns {*} The converted value, or null
 */
function convertVariableValue(field, calcVar) {
  const val = calcVar.value;
  if (val == null || val === '') return null;

  switch (field.type) {
    case 'text':
    case 'textarea':
      return String(val);

    case 'select':
    case 'radio_group': {
      if (field.options) {
        const strVal = String(val);
        const match = field.options.find(o => o.value === strVal || o.display === strVal);
        if (match) return match.value;
      }
      return String(val);
    }

    case 'checkbox':
      return Boolean(val);

    default:
      return String(val);
  }
}
